import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Alert, Checkbox, IconButton, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import { size } from "lodash";
import { useParams } from "react-router-dom";
import chatApi from "../../../../../api/chatApi";
import {
  useDBTInterruptActions,
  useDBTInterruptStore,
} from "../../../../../store/useDBTInterruptStore";
import {
  useSessionActions,
  useSessionStore,
} from "../../../../../store/useSessionStore";
import { StyledAGGrid } from "../../../BrightbotStateSidebar/style";
import useChatStream from "../../../ChatBot/hooks/useChatStream";
import {
  ButtonsContainer,
  CancelButton,
  CodeBlock,
  FieldsContainer,
  FormContainer,
  FormTitle,
  GridContainer,
  GridHeaderContainer,
  PreviewContainer,
  PreviewSection,
  PreviewTitle,
  StyledCard,
  StyledCardContent,
  StyledFormControlLabel,
  StyledTextField,
  SubmitButton,
} from "./style";

interface FormData {
  approval_status: boolean;
  convention_description: string;
  suggested_file_name: string;
  suggested_table_name: string;
  current_table_names_in_external_schema: string[];
}

interface FormErrors {
  approval_status?: string;
  convention_description?: string;
  suggested_file_name?: string;
  suggested_table_name?: string;
  current_table_names_in_external_schema?: string;
}

const CommitModelsForm = () => {
  const { threadId } = useParams();
  const activeRunId = useSessionStore.use.activeRunId();
  const thread = useSessionStore.use.thread();
  const interruptAction = useSessionStore.use.interruptAction();
  const commitModelsConfig = useDBTInterruptStore.use.commit_models_config();
  const commitKwargs = commitModelsConfig?.additional_kwargs;

  const { streamHITLHandler } = useChatStream();
  const { setDBTCommitModelsConfigValues } = useDBTInterruptActions();
  const { setActiveRunId, setStream, setInterruptAction, clearSteps } =
    useSessionActions();

  const gridRef = useRef<any>(null);

  // Get initial values from selected_data
  const getInitialFormData = (): FormData => {
    const selectedDataArray = commitModelsConfig?.name_for_file_and_source_table?.selected_data;
    const selectedData = Array.isArray(selectedDataArray) ? selectedDataArray[0] : null;
    return {
      approval_status: false,
      convention_description: "",
      suggested_file_name: selectedData?.suggested_file_name || "",
      suggested_table_name: selectedData?.suggested_table_name || "",
      current_table_names_in_external_schema: selectedData?.current_table_names_in_external_schema || [],

    };
  };

  // Get additional data from selected_data (it's an array with one object)
  const selectedDataArray = commitModelsConfig?.name_for_file_and_source_table?.selected_data;
  const selectedData = Array.isArray(selectedDataArray) ? selectedDataArray[0] : null;
  
  const newTableDetected = selectedData?.new_table_detected === true;
  const currentTableNames: string[] = Array.isArray(selectedData?.current_table_names_in_external_schema) 
    ? selectedData.current_table_names_in_external_schema 
    : [];


  // Local form state
  const [formData, setFormData] = useState<FormData>(getInitialFormData());

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTableNameWarning, setShowTableNameWarning] = useState(false);

  // Check for table name conflicts when newTableDetected and table name changes
  useEffect(() => {
    if (newTableDetected && formData.suggested_table_name) {
      if (currentTableNames.includes(formData.suggested_table_name)) {
        setShowTableNameWarning(true);
      } else {
        setShowTableNameWarning(false);
      }
    } else {
      setShowTableNameWarning(false);
    }
  }, [newTableDetected, formData.suggested_table_name, currentTableNames]);

  const handleExportCSV = () => {
    if (gridRef.current) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `dbt-commit-transformation-${Date.now()}.csv`,
      });
    }
  };

  // Validation functions
  const validateConventionDescription = (
    description: string
  ): string | undefined => {
    if (!description.trim()) {
      return "Convention description is required";
    }

    if (description.length < 5) {
      return "Convention description must be at least 5 characters";
    }

    return undefined;
  };

  const validateApprovalStatus = (status: boolean): string | undefined => {
    if (!status) {
      return "You must approve the changes to proceed";
    }

    return undefined;
  };

  const validateFileName = (fileName: string): string | undefined => {
    if (!fileName.trim()) {
      return "Model name is required";
    }

    if (fileName.length < 2) {
      return "Model name must be at least 2 characters";
    }

    return undefined;
  };

  const validateTableName = (tableName: string): string | undefined => {
    if (!newTableDetected) {
      // If no table detection required, don't validate
      return undefined;
    }

    if (!tableName.trim()) {
      return "Table name is required";
    }

    if (tableName.length < 2) {
      return "Table name must be at least 2 characters";
    }

    // Check if the table name conflicts with existing names
    if (currentTableNames.includes(tableName.trim())) {
      setShowTableNameWarning(true);
      return "This table name already exists. Please choose a different name.";
    } else {
      setShowTableNameWarning(false);
    }

    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const descriptionError = validateConventionDescription(
      formData.convention_description
    );
    if (descriptionError) newErrors.convention_description = descriptionError;

    const approvalError = validateApprovalStatus(formData.approval_status);
    if (approvalError) newErrors.approval_status = approvalError;

    const fileNameError = validateFileName(formData.suggested_file_name);
    if (fileNameError) newErrors.suggested_file_name = fileNameError;

    const tableNameError = validateTableName(formData.suggested_table_name);
    if (tableNameError) newErrors.suggested_table_name = tableNameError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange =
    (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        field === "approval_status" ? event.target.checked : event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear error for this field when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Map form fields to store structure
      const configData = {
        approval_status: formData.approval_status,
        convention_description: formData.convention_description,
        name_for_file_and_source_table: {
          suggested_file_name: formData.suggested_file_name,
          ...(newTableDetected && { suggested_table_name: formData.suggested_table_name }),
        },
      };

      // Push to store
      setDBTCommitModelsConfigValues(configData);

      // Handle form submission logic

      await streamHITLHandler(threadId, configData);
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const abortMessageHandler = async () => {
    if (!activeRunId) return;
    const currentThread = thread || threadId;
    // stop Interrupt Action if available

    try {
      await chatApi.post(
        `/threads/${currentThread}/runs/${activeRunId}/cancel?wait=false&action=interrupt`
      );
    } catch (err) {
      console.log(err);
    } finally {
      // clear steps and interrupt action
      clearSteps();
      if (interruptAction) {
        setInterruptAction(null);
      }
      setStream(false);
      setActiveRunId(undefined);
    }
  };

  const handleCancel = async () => {
    // Reset form on cancel
    setFormData(getInitialFormData());
    setErrors({});
    await abortMessageHandler();
    setInterruptAction(null);
  };

  const isFormValid =
    formData.approval_status &&
    formData.convention_description.trim() &&
    formData.suggested_file_name.trim() &&
    (!newTableDetected || formData.suggested_table_name.trim()) &&
    Object.keys(errors).length === 0;

  if (isSubmitting) {
    return null;
  }

  return (
    <FormContainer>
      <StyledCard>
        <FormTitle variant="h6">DBT Models Review & Commit</FormTitle>
        <StyledCardContent>
          {commitKwargs && (
            <PreviewContainer>
              {commitKwargs.summary && (
                <PreviewSection>
                  <PreviewTitle variant="subtitle2">Summary:</PreviewTitle>
                  <Typography variant="body2" color="text.secondary">
                    {commitKwargs.summary}
                  </Typography>
                </PreviewSection>
              )}

              {commitKwargs.sql && (
                <PreviewSection>
                  <PreviewTitle variant="subtitle2">
                    Generated SQL:
                  </PreviewTitle>
                  <CodeBlock>{commitKwargs.sql}</CodeBlock>
                </PreviewSection>
              )}

              {commitKwargs.resulted_transformation &&
                commitKwargs.resulted_transformation.length > 0 && (
                  <PreviewSection>
                    <GridHeaderContainer>
                      <Typography variant="body2" fontWeight="bold">
                        Applied Transformations
                      </Typography>
                      {size(commitKwargs.resulted_transformation) > 0 && (
                        <Tooltip title="Export as CSV">
                          <IconButton onClick={handleExportCSV} size="small">
                            <FileDownloadIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </GridHeaderContainer>
                    <GridContainer onClick={(e) => e.stopPropagation()}>
                      <StyledAGGrid
                        ref={gridRef}
                        className="ag-theme-material top-border-0"
                        suppressContextMenu
                        suppressMovableColumns
                        suppressDragLeaveHidesColumns
                        pagination
                        headerHeight={40}
                        rowHeight={40}
                        rowData={commitKwargs.resulted_transformation}
                        columnDefs={Object.keys(
                          commitKwargs.resulted_transformation[0]
                        ).map((key) => ({
                          field: key,
                        }))}
                      />
                    </GridContainer>
                  </PreviewSection>
                )}
            </PreviewContainer>
          )}

          <FieldsContainer>
            <StyledTextField
              fullWidth
              label="What should your model name be?"
              variant="outlined"
              size="small"
              value={formData.suggested_file_name}
              onChange={handleInputChange("suggested_file_name")}
              error={!!errors.suggested_file_name}
              helperText={errors.suggested_file_name}
              placeholder="Enter the name for your DBT model file..."
              hasError={!!errors.suggested_file_name}
            />

            {newTableDetected && (
              <>
                <StyledTextField
                  fullWidth
                  label="What should we name the raw table you retrieved in this session?"
                  variant="outlined"
                  size="small"
                  value={formData.suggested_table_name}
                  onChange={handleInputChange("suggested_table_name")}
                  error={!!errors.suggested_table_name}
                  helperText={errors.suggested_table_name}
                  placeholder="Enter the name for the raw table..."
                  hasError={!!errors.suggested_table_name}
                />
                {showTableNameWarning && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    This table name conflicts with an existing table. Please choose a different name.
                  </Alert>
                )}
              </>
            )}

            <StyledTextField
              fullWidth
              label="Convention Description"
              variant="outlined"
              size="small"
              multiline
              rows={3}
              value={formData.convention_description}
              onChange={handleInputChange("convention_description")}
              error={!!errors.convention_description}
              helperText={errors.convention_description}
              placeholder="Describe the naming conventions or standards applied..."
              hasError={!!errors.convention_description}
            />

            <StyledFormControlLabel
              control={
                <Checkbox
                  checked={formData.approval_status}
                  onChange={handleInputChange("approval_status")}
                  color="primary"
                />
              }
              label="I approve these changes and want to commit the models"
            />
            {errors.approval_status && (
              <Typography
                variant="body2"
                color="error"
                sx={{ marginTop: -0.5, marginLeft: 2 }}
              >
                {errors.approval_status}
              </Typography>
            )}
          </FieldsContainer>

          <ButtonsContainer>
            <CancelButton
              variant="outlined"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </CancelButton>
            <SubmitButton
              variant="contained"
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? "Committing..." : "Commit Models"}
            </SubmitButton>
          </ButtonsContainer>
        </StyledCardContent>
      </StyledCard>
    </FormContainer>
  );
};

export default CommitModelsForm;
